import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelAudioGeneration, createAudioGeneration, type AudioGenerationDoneEvent, type AudioGenerationFailedEvent, openAudioGenerationStatusStream, type AudioGenerationCreateResponse, type AudioGenerationSseEvent, type AudioGenerationStep } from '#speaker-shop/utils/audio-generation-api.client.ts';

interface UseAudioGenerationControllerOptions {
    voiceSlug?: string;
    speakerVoiceId?: string;
    onGenerationCreated?: (jobId: string, steps: AudioGenerationStep[]) => void;
    onStageChange?: (stage: 'IN_QUEUE' | 'MODERATION' | 'GENERATION' | 'DONE') => void;
    onQueuePositionChange?: (queuePosition: number) => void;
    onGenerationDone?: (event: AudioGenerationDoneEvent, text: string) => void;
    onGenerationFailed?: (event: AudioGenerationFailedEvent, text: string, jobId: string) => void;
    onGenerationSettled?: () => void;
}

export function useAudioGenerationController({
    voiceSlug,
    speakerVoiceId,
    onGenerationCreated,
    onStageChange,
    onQueuePositionChange,
    onGenerationDone,
    onGenerationFailed,
    onGenerationSettled,
}: UseAudioGenerationControllerOptions) {
    const [ text, setText ] = useState('');
    const [ characterCount, setCharacterCount ] = useState(0);
    const [ jobId, setJobId ] = useState<string | null>(null);
    const [ cancelToken, setCancelToken ] = useState<string | null>(null);
    const [ isCancelling, setIsCancelling ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ isGenerating, setIsGenerating ] = useState(false);
    const [ lastBlockedText, setLastBlockedText ] = useState<string | null>(null);
    const [ isFlagged, setIsFlagged ] = useState(false);
    const [ flaggedJobId, setFlaggedJobId ] = useState<string | null>(null);
    const [ flaggedCategories, setFlaggedCategories ] = useState<string[]>([]);
    const currentTextRef = useRef('');
    const onStageChangeRef = useRef(onStageChange);
    const onQueuePositionChangeRef = useRef(onQueuePositionChange);
    const onGenerationDoneRef = useRef(onGenerationDone);
    const onGenerationFailedRef = useRef(onGenerationFailed);
    const onGenerationSettledRef = useRef(onGenerationSettled);

    useEffect(() => {
        onStageChangeRef.current = onStageChange;
        onQueuePositionChangeRef.current = onQueuePositionChange;
        onGenerationDoneRef.current = onGenerationDone;
        onGenerationFailedRef.current = onGenerationFailed;
        onGenerationSettledRef.current = onGenerationSettled;
    }, [ onGenerationDone, onGenerationFailed, onGenerationSettled, onQueuePositionChange, onStageChange ]);

    const blockedAndUnchanged = !!lastBlockedText && text.trim() === lastBlockedText.trim();

    const updateText = useCallback((value: string) => {
        setText(value);
        setCharacterCount(value.length);

        if (lastBlockedText && value.trim() !== lastBlockedText.trim()) {
            setIsFlagged(false);
            setError(null);
        }
    }, [ lastBlockedText ]);

    const submitGeneration = useCallback(async (voiceSettings?: unknown) => {
        if (isGenerating) {
            return null;
        }

        const trimmedText = text.trim();
        if (!trimmedText) {
            return null;
        }

        currentTextRef.current = trimmedText;
        setJobId(null);
        setCancelToken(null);
        setIsCancelling(false);
        setError(null);
        setIsFlagged(false);
        setFlaggedCategories([]);
        setFlaggedJobId(null);
        setLastBlockedText(null);
        setIsGenerating(true);

        try {
            const data = await createAudioGeneration(
                trimmedText,
                { voiceSlug, speakerVoiceId, voiceSettings }
            ) as AudioGenerationCreateResponse;

            if ('jobId' in data) {
                setJobId(data.jobId);
                setCancelToken(data.cancelToken);
                onGenerationCreated?.(data.jobId, data.steps);
                return data;
            }

            if ('limitReached' in data && data.limitReached) {
                let message = data.error;

                if (!message) {
                    switch (data.reason) {
                        case 'character_limit':
                            message = 'Character limit reached.';
                            break;
                        case 'daily_limit':
                            message = 'Daily generation limit reached.';
                            break;
                        case 'no_cookie_fallback':
                            message = 'Cookie consent required to use this service.';
                            break;
                        default:
                            message = 'Generation limit reached.';
                    }
                }

                setError(message);
                setIsGenerating(false);
                onGenerationSettledRef.current?.();
                return data;
            }

            setError(data.error ?? 'Failed to create job.');
            setIsGenerating(false);
            onGenerationSettledRef.current?.();
            return data;
        } catch (submissionError) {
            console.error('Failed to create audio generation job:', submissionError);
            setError('Failed to create job.');
            setIsGenerating(false);
            onGenerationSettledRef.current?.();
            return null;
        }
    }, [ isGenerating, onGenerationCreated, speakerVoiceId, text, voiceSlug ]);

    useEffect(() => {
        if (!isGenerating || !jobId || !cancelToken) {
            return;
        }

        const sseJobId = jobId;
        const submittedText = currentTextRef.current;
        const eventSource = openAudioGenerationStatusStream(sseJobId, cancelToken);

        eventSource.onopen = () => {
            setError(null);
            setFlaggedJobId(null);
        };

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data) as AudioGenerationSseEvent;

                if (data.status === 'IN_QUEUE') {
                    onStageChangeRef.current?.('IN_QUEUE');
                    onQueuePositionChangeRef.current?.(data.queuePosition || 0);
                } else if (data.status === 'MODERATION') {
                    onStageChangeRef.current?.('MODERATION');
                } else if (data.status === 'GENERATION') {
                    onStageChangeRef.current?.('GENERATION');
                }

                switch (data.status) {
                    case 'CANCELLED':
                        eventSource.close();
                        setJobId(prev => prev === sseJobId ? null : prev);
                        setCancelToken(null);
                        setIsCancelling(false);
                        setIsGenerating(false);
                        onGenerationSettledRef.current?.();
                        break;

                    case 'DONE':
                        eventSource.close();
                        onStageChangeRef.current?.('DONE');
                        onGenerationDoneRef.current?.(data, submittedText);
                        setJobId(prev => prev === sseJobId ? null : prev);
                        setTimeout(() => {
                            setIsGenerating(false);
                            onGenerationSettledRef.current?.();
                        }, 1000);
                        break;

                    case 'FAILED':
                        eventSource.close();
                        if (data.classifierResult) {
                            if (data.classifierResult.type === 'flaggedByModeration') {
                                setFlaggedCategories(data.classifierResult.categories || []);
                            } else if (data.classifierResult.type === 'classified') {
                                setFlaggedCategories(data.classifierResult.reasons || []);
                            }
                            setIsFlagged(true);
                            setFlaggedJobId(sseJobId);
                            setLastBlockedText(submittedText);
                        }
                        setError(data.error || 'Job failed');
                        onGenerationFailedRef.current?.(data, submittedText, sseJobId);
                        setJobId(prev => prev === sseJobId ? null : prev);
                        setTimeout(() => {
                            setIsGenerating(false);
                            onGenerationSettledRef.current?.();
                        }, 1000);
                        break;

                    case 'ERROR':
                    case 'TIMEOUT':
                        eventSource.close();
                        setError(data.error || 'Job failed');
                        setJobId(prev => prev === sseJobId ? null : prev);
                        setTimeout(() => {
                            setIsGenerating(false);
                            onGenerationSettledRef.current?.();
                        }, 1000);
                        break;

                    case 'NOT_FOUND':
                        eventSource.close();
                        setError('Job not found');
                        setJobId(prev => prev === sseJobId ? null : prev);
                        setTimeout(() => {
                            setIsGenerating(false);
                            onGenerationSettledRef.current?.();
                        }, 1000);
                        break;
                }
            } catch (parseError) {
                console.error('Failed to parse SSE message:', parseError);
                setError('Received invalid data from server. Please try again.');
            }
        };

        eventSource.onerror = () => {
            eventSource.close();
            setError('Connection lost. Please try again.');
            setJobId(prev => prev === sseJobId ? null : prev);
            setTimeout(() => {
                setIsGenerating(false);
                onGenerationSettledRef.current?.();
            }, 1000);
        };

        return () => {
            eventSource.close();
        };
    }, [ cancelToken, isGenerating, jobId ]);

    const cancelGeneration = useCallback(async () => {
        if (!jobId || !cancelToken || isCancelling) {
            return;
        }
        setIsCancelling(true);
        try {
            await cancelAudioGeneration(jobId, cancelToken);
        } catch (cancelError) {
            console.error('Failed to cancel audio generation:', cancelError);
            setError('Failed to cancel generation.');
            setIsCancelling(false);
        }
    }, [ cancelToken, isCancelling, jobId ]);

    return {
        text,
        characterCount,
        error,
        isGenerating,
        isCancelling,
        blockedAndUnchanged,
        isFlagged,
        flaggedJobId,
        flaggedCategories,
        updateText,
        submitGeneration,
        cancelGeneration,
        setIsFlagged
    };
}
